import type { FilterModel, SortModelItem } from "~/components/grid/AgGrid";
import type { GraphQLFilter, SpecialFieldHandler } from "~/helpers/ag-grid/convertAgGridFilterToGraphQL";
import type { FilterType } from "~/helpers/ag-grid/createFilteredColumn";
import type { FulfillOrderRow } from "~fulfill/orders/helpers/fulfillOrderRow";

import {
	type CombinedOrderDocumentFilterInput,
	type CombinedOrderDocumentSortInput,
	type GridRequestInput,
	OrderInvoiceStatus,
	OrderStatus,
	OrderType,
	SortEnumType
} from "~/gql/graphqlBase.generated";
import addSearchFilter from "~/helpers/ag-grid/addSearchFilter";
import combineFilters from "~/helpers/ag-grid/combineFilters";
import convertAgGridFilterToGraphQL from "~/helpers/ag-grid/convertAgGridFilterToGraphQL";
import unwrapMultiFilterModel from "~/helpers/ag-grid/unwrapMultiFilterModel";
import { isCreditApplicationStatus, resolveOrderMoneyStatus } from "~/helpers/creditApplicationStatus";
import archivedRecordsScope from "~/helpers/grid/archivedRecordsScope";
import { stripSyntheticGroupSort } from "~/helpers/grid/createSsrmDatasource";
export const COMBINED_ORDERS_GRID_ID = "combinedOrders";

export const SEARCH_FIELDS = ["customerName", "salesPerson", "poNumber", "orderEntityNumber", "erpId"];

const SALES_PERSON_COL = "salesPerson";
const STATUS_COL = "status";

export const DEFAULT_HIDDEN_STATUSES: readonly OrderStatus[] = [OrderStatus.Deleted, OrderStatus.Initialized];
export const DELETED_ONLY_HIDDEN_STATUSES: readonly OrderStatus[] = [OrderStatus.Deleted];

interface SetFilterModelEntry {
	filterType?: string;
	values?: (string | null)[];
}

export interface StatusScopeOptions {
	includeInitialized?: boolean;
}

export const visibleStatusValues = (includeInitialized = false): OrderStatus[] => {
	const hidden = new Set<OrderStatus>(includeInitialized ? DELETED_ONLY_HIDDEN_STATUSES : DEFAULT_HIDDEN_STATUSES);
	return Object.values(OrderStatus).filter((status) => !hidden.has(status));
};

export const withSalesPersonScope = (filterModel: unknown, salesPerson: string | undefined): unknown => {
	if (!salesPerson) return filterModel ?? null;
	const scope = { filter: salesPerson, filterType: "text", type: "contains" };
	const model = (filterModel as Record<string, unknown> | null) ?? {};
	const existing = model[SALES_PERSON_COL];
	const scoped = existing ? { conditions: [existing, scope], filterType: "text", operator: "AND" } : scope;
	return { ...model, [SALES_PERSON_COL]: scoped };
};

export const withDefaultStatusScope = (
	filterModel: unknown,
	includeDeleted: boolean,
	options?: StatusScopeOptions
): unknown => {
	const includeInitialized = options?.includeInitialized ?? false;
	return archivedRecordsScope.scopeFilterModel(filterModel, includeDeleted, {
		hiddenStatuses: includeInitialized ? DELETED_ONLY_HIDDEN_STATUSES : DEFAULT_HIDDEN_STATUSES,
		statusField: STATUS_COL,
		visibleStatuses: visibleStatusValues(includeInitialized)
	});
};

export interface OrdersDatasourceScope {
	getIncludeDeleted: () => boolean;
	getIncludeInitialized?: () => boolean;
	getSalesPersonScope: () => string | undefined;
}

// wsapi GridRegistry reads colId orderTotal as the royalty-inclusive OrderTotal; the Total column shows customerTotal.
const toServerColId = (colId: string): string => (colId === "orderTotal" ? "customerTotal" : colId);

const withServerColIds = (request: GridRequestInput): GridRequestInput => ({
	...request,
	filterModel:
		request.filterModel && typeof request.filterModel === "object"
			? Object.fromEntries(Object.entries(request.filterModel).map(([colId, model]) => [toServerColId(colId), model]))
			: request.filterModel,
	sortModel: request.sortModel.map((item) => ({ ...item, colId: toServerColId(item.colId) })),
	valueCols: request.valueCols.map((col) => ({ ...col, field: toServerColId(col.field) }))
});

export const createOrdersDatasourceTransform =
	({ getIncludeDeleted, getIncludeInitialized, getSalesPersonScope }: OrdersDatasourceScope) =>
	(request: GridRequestInput): GridRequestInput =>
		withServerColIds({
			...request,
			filterModel: withDefaultStatusScope(
				withSalesPersonScope(toServerFilterModel(request.filterModel), getSalesPersonScope()),
				getIncludeDeleted(),
				{ includeInitialized: getIncludeInitialized?.() }
			)
		});

export const createOrdersSetValuesTransform =
	(getSalesPersonScope: (() => string | undefined) | undefined) =>
	(request: GridRequestInput): GridRequestInput =>
		withServerColIds({
			...request,
			filterModel: withSalesPersonScope(toServerFilterModel(request.filterModel), getSalesPersonScope?.())
		});

export const ORDERS_RESOLVE_UNIQUE_TIEBREAKER_SORT: GridRequestInput["sortModel"] = [
	{ colId: "orderEntityNumber", sort: "desc" }
];

export const withOrdersResolveUniqueTiebreakerSort = (request: GridRequestInput): GridRequestInput =>
	stripSyntheticGroupSort(request.sortModel).length > 0
		? request
		: { ...request, sortModel: ORDERS_RESOLVE_UNIQUE_TIEBREAKER_SORT };

const COMPUTED_COLUMNS = new Set(["termsExpirationDays"]);

export const ORDERS_MULTI_FILTER_PRIMARY_TYPE: Record<
	"customerName" | "erpId" | "poNumber" | "salesPerson" | "shipFromSiteName" | "weekNumber",
	"number" | "text"
> = {
	customerName: "text",
	erpId: "text",
	poNumber: "text",
	salesPerson: "text",
	shipFromSiteName: "text",
	weekNumber: "number"
};

export const FIELD_TYPE_MAP: Record<string, FilterType> = {
	allocationStatus: "set",
	customerId: "text",
	...ORDERS_MULTI_FILTER_PRIMARY_TYPE,
	expectedDeliveryDate: "dateOnly",
	finalCredit: "bool",
	initializedDate: "date",
	invoiceNumber: "text",
	invoiceStatus: "set",
	lastAllocatedDate: "date",
	numberOfLines: "number",
	onHold: "bool",
	orderEntityId: "text",
	orderEntityNumber: "text",
	orderKindId: "set",
	orderType: "set",
	program: "set",
	shippedDate: "date",
	source: "set",
	status: "set",
	submittedAt: "date",
	syncDate: "date"
};

const NUMBER_FILTER_COL_IDS: ReadonlySet<string> = new Set(
	Object.entries(FIELD_TYPE_MAP)
		.filter(([, filterType]) => filterType === "number")
		.map(([colId]) => colId)
);

export const toServerFilterModel = (filterModel: unknown): unknown =>
	unwrapMultiFilterModel(filterModel, NUMBER_FILTER_COL_IDS);

const moneyCondition = (
	field: "customerTotal" | "freightAmount",
	type: string | undefined,
	filter: number | string | null | undefined,
	filterTo: number | string | null | undefined
): GraphQLFilter | null => {
	if (filter == null && type !== "blank" && type !== "notBlank") return null;
	const amount = Number(filter);
	switch (type) {
		case "equals":
			return { [field]: { amount: { eq: amount } } };
		case "notEqual":
			return { [field]: { amount: { neq: amount } } };
		case "greaterThan":
			return { [field]: { amount: { gt: amount } } };
		case "greaterThanOrEqual":
			return { [field]: { amount: { gte: amount } } };
		case "lessThan":
			return { [field]: { amount: { lt: amount } } };
		case "lessThanOrEqual":
			return { [field]: { amount: { lte: amount } } };
		case "inRange":
			return {
				and: [{ [field]: { amount: { gte: amount } } }, { [field]: { amount: { lte: Number(filterTo) } } }]
			};
		case "blank":
			return { [field]: { amount: { eq: null } } };
		case "notBlank":
			return { [field]: { amount: { neq: null } } };
		default:
			return { [field]: { amount: { eq: amount } } };
	}
};

const moneyHandler =
	(field: "customerTotal" | "freightAmount"): SpecialFieldHandler =>
	(filterDef) => {
		if (filterDef.operator && filterDef.conditions) {
			const subConditions = filterDef.conditions
				.map((cond) => moneyCondition(field, cond.type, cond.filter, cond.filterTo))
				.filter((condition): condition is GraphQLFilter => condition !== null);
			return subConditions.length > 0 ? [{ [filterDef.operator.toLowerCase()]: subConditions }] : [];
		}
		const condition = moneyCondition(field, filterDef.type, filterDef.filter, filterDef.filterTo);
		return condition ? [condition] : [];
	};

interface AmountCondition {
	filter?: number | string | null;
	filterTo?: number | string | null;
	type?: string;
}

interface AmountFilter extends AmountCondition {
	conditions?: AmountCondition[];
	operator?: string;
}

const PAYMENT_AMOUNT_COLUMNS = ["amountPaid", "balance"] as const;

const matchesAmountCondition = (amount: number | null, { filter, filterTo, type }: AmountCondition): boolean => {
	if (type === "blank") return amount == null;
	if (type === "notBlank") return amount != null;
	const value = Number(filter);
	if (type === "notEqual") return amount !== value;
	if (amount == null) return false;
	switch (type) {
		case "greaterThan":
			return amount > value;
		case "greaterThanOrEqual":
			return amount >= value;
		case "lessThan":
			return amount < value;
		case "lessThanOrEqual":
			return amount <= value;
		case "inRange":
			return amount >= value && amount <= Number(filterTo);
		default:
			return amount === value;
	}
};

const matchesAmountFilter = (amount: number | null, filterDef: AmountFilter): boolean => {
	if (typeof filterDef.operator === "string" && Array.isArray(filterDef.conditions)) {
		const matches = (condition: AmountCondition) => matchesAmountCondition(amount, condition);
		return filterDef.operator === "OR" ? filterDef.conditions.some(matches) : filterDef.conditions.every(matches);
	}
	return matchesAmountCondition(amount, filterDef);
};

export const matchesPaymentAmountFilters = (
	row: Pick<FulfillOrderRow, "amountPaid" | "balance">,
	filterModel: FilterModel
): boolean =>
	PAYMENT_AMOUNT_COLUMNS.every((colId) => !filterModel[colId] || matchesAmountFilter(row[colId], filterModel[colId]));

// combinedOrders filters Option money fields by presence only, so the export applies these amounts to the fetched rows.
const appliedToFetchedRows: SpecialFieldHandler = () => [];

const CREDIT_ORDER: GraphQLFilter = { orderType: { eq: OrderType.Credit } };

const isCreditMemo = (value: unknown): boolean => value === OrderInvoiceStatus.CreditMemo;

// combinedOrders matches the stored status, which never holds a reading only a credit order shows.
const storedStatusOrCreditOrder =
	(field: "invoiceStatus" | "paymentStatus", isCreditReading: (value: unknown) => boolean): SpecialFieldHandler =>
	({ values }) => {
		if (!values) return [];
		const stored = { [field]: { in: values.filter((value) => !isCreditReading(value)) } };
		return [values.some(isCreditReading) ? { or: [stored, CREDIT_ORDER] } : stored];
	};

const matchesSetSelection = (value: null | string | undefined, filterDef: SetFilterModelEntry | undefined): boolean =>
	!filterDef?.values || filterDef.values.includes(value ?? null);

export const matchesShownStatusFilters = (
	row: Pick<FulfillOrderRow, "creditApplicationStatus" | "invoiceStatus" | "orderType" | "paymentStatus">,
	filterModel: FilterModel
): boolean =>
	matchesSetSelection(resolveOrderMoneyStatus(row)?.status, filterModel.paymentStatus) &&
	matchesSetSelection(row.invoiceStatus, filterModel.invoiceStatus);

const confirmationEmailCondition = (
	type: string | undefined,
	filter: number | string | null | undefined
): GraphQLFilter | null => {
	if (filter == null && type !== "blank" && type !== "notBlank") return null;
	switch (type) {
		case "equals":
			return { confirmationEmail: { toEmail: { eq: filter } } };
		case "notEqual":
			return { confirmationEmail: { toEmail: { neq: filter } } };
		case "notContains":
			return { confirmationEmail: { toEmail: { ncontains: filter } } };
		case "startsWith":
			return { confirmationEmail: { toEmail: { startsWith: filter } } };
		case "endsWith":
			return { confirmationEmail: { toEmail: { endsWith: filter } } };
		case "blank":
			return { confirmationEmail: { toEmail: { eq: null } } };
		case "notBlank":
			return { confirmationEmail: { toEmail: { neq: null } } };
		default:
			return { confirmationEmail: { toEmail: { contains: filter } } };
	}
};

const confirmationEmailHandler: SpecialFieldHandler = (filterDef) => {
	if (filterDef.operator && filterDef.conditions) {
		const subConditions = filterDef.conditions
			.map((cond) => confirmationEmailCondition(cond.type, cond.filter))
			.filter((condition): condition is GraphQLFilter => condition !== null);
		return subConditions.length > 0 ? [{ [filterDef.operator.toLowerCase()]: subConditions }] : [];
	}
	const condition = confirmationEmailCondition(filterDef.type, filterDef.filter);
	return condition ? [condition] : [];
};

type AgDateFilterDef = Parameters<SpecialFieldHandler>[0];

const LOCAL_DATE_LENGTH = 10;

const localDateOf = (value: string | null | undefined): string | null =>
	value ? value.slice(0, LOCAL_DATE_LENGTH) : null;

const localDateCondition = (field: string, condition: AgDateFilterDef): GraphQLFilter | null => {
	const from = localDateOf(condition.dateFrom);
	const to = localDateOf(condition.dateTo);
	switch (condition.type) {
		case "blank":
			return { [field]: { eq: null } };
		case "notBlank":
			return { [field]: { neq: null } };
		case "equals":
			return from ? { [field]: { eq: from } } : null;
		case "notEqual":
			return from ? { or: [{ [field]: { lt: from } }, { [field]: { gt: from } }] } : null;
		case "lessThan":
			return from ? { [field]: { lt: from } } : null;
		case "greaterThan":
			return from ? { [field]: { gt: from } } : null;
		case "inRange":
			return from && to ? { and: [{ [field]: { gte: from } }, { [field]: { lte: to } }] } : null;
		default:
			return null;
	}
};

// combinedOrders LocalDate operators reject date-time strings.
const localDateHandler =
	(field: string): SpecialFieldHandler =>
	(filterDef) => {
		if (filterDef.operator && filterDef.conditions) {
			const conditions = filterDef.conditions
				.map((condition) => localDateCondition(field, condition))
				.filter((condition): condition is GraphQLFilter => condition !== null);
			return conditions.length > 0 ? [{ [filterDef.operator.toLowerCase()]: conditions }] : [];
		}
		const condition = localDateCondition(field, filterDef);
		return condition ? [condition] : [];
	};

export const SPECIAL_FIELD_HANDLERS: Record<string, SpecialFieldHandler> = {
	amountPaid: appliedToFetchedRows,
	balance: appliedToFetchedRows,
	confirmationEmail: confirmationEmailHandler,
	followUpOn: localDateHandler("followUpOn"),
	freightAmount: moneyHandler("freightAmount"),
	invoiceStatus: storedStatusOrCreditOrder("invoiceStatus", isCreditMemo),
	orderTotal: moneyHandler("customerTotal"),
	paymentStatus: storedStatusOrCreditOrder("paymentStatus", isCreditApplicationStatus),
	quoteValidUntil: localDateHandler("quoteValidUntil")
};

export const convertSortModel = (sortModel: SortModelItem[]): CombinedOrderDocumentSortInput[] =>
	sortModel
		.filter((item) => !COMPUTED_COLUMNS.has(item.colId))
		.map((item) => {
			const direction = item.sort === "desc" ? SortEnumType.Desc : SortEnumType.Asc;
			if (item.colId === "orderTotal") return { customerTotal: { amount: direction } };
			if (item.colId === "freightAmount") return { freightAmount: { amount: direction } };
			if (item.colId === "confirmationEmail") return { confirmationEmail: { toEmail: direction } };
			return { [item.colId]: direction } as CombinedOrderDocumentSortInput;
		});

export const buildWhere = (
	filterModel: FilterModel | null | undefined,
	baseFilter: GraphQLFilter | undefined,
	searchText: string | undefined
): CombinedOrderDocumentFilterInput | undefined => {
	const columnFilter = convertAgGridFilterToGraphQL(filterModel, FIELD_TYPE_MAP, SPECIAL_FIELD_HANDLERS);
	const searchFilter = addSearchFilter(searchText, SEARCH_FIELDS);
	const userFilter = combineFilters(searchFilter, columnFilter);
	return combineFilters(baseFilter, userFilter) as CombinedOrderDocumentFilterInput | undefined;
};
